import { useEffect, useRef, useState } from 'react';
import UiError from '../../components/UiError';
import doneSound from '../../sounds/bell.wav';
import DatasetConsent from '../dataset/DatasetConsent';
import { getDatasetConsent, setDatasetConsent } from '../dataset/consent';
import { useDatasetContribution } from '../dataset/useDatasetContribution';
import { useDatasetRuntimeConfig } from '../dataset/runtimeConfig';
import '../dataset/dataset.css';
import FilePicker from './FilePicker';
import { getErrorDiagnostics } from './errors';
import { LANGUAGES } from './languages';
import { getMediaFileDiagnostics } from './mediaFormats';
import RangeSelector from './RangeSelector';
import { canStartTranscription, TRANSCRIPTION_STATUS } from './sessionState';
import TranscriptionResult from './TranscriptionResult';
import TranscriptionProgress from './TranscriptionProgress';
import { useMinimumProgress } from './useMinimumProgress';
import { useTranscriptionSession } from './useTranscriptionSession';
import './transcription.css';

const ACTIVE = new Set([TRANSCRIPTION_STATUS.PREPARING, TRANSCRIPTION_STATUS.TRANSCRIBING]);
const FINISH_SOUND_STORAGE_KEY = 'activateFinishAudio';

export default function TranscriptionWorkspace({ isPremium, language, mode, onLanguageChange, onModeChange, onRequirePremium, userData }) {
  const session = useTranscriptionSession();
  const [fileError, setFileError] = useState(null);
  const [datasetAccepted, setDatasetAcceptedState] = useState(getDatasetConsent);
  const datasetEnabled = useDatasetRuntimeConfig();
  const { contribute } = useDatasetContribution();
  const holdingResult = useMinimumProgress(session.status);
  const status = holdingResult ? TRANSCRIPTION_STATUS.TRANSCRIBING : session.status;
  const active = ACTIVE.has(status);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem(FINISH_SOUND_STORAGE_KEY) === 'true');
  const previousStatus = useRef(status);
  const shouldContribute = datasetEnabled && datasetAccepted && language === 'ca' && !isPremium;

  const changeSound = (enabled) => {
    setSoundEnabled(enabled);
    localStorage.setItem(FINISH_SOUND_STORAGE_KEY, String(enabled));
  };

  useEffect(() => {
    if (previousStatus.current !== TRANSCRIPTION_STATUS.COMPLETE
      && status === TRANSCRIPTION_STATUS.COMPLETE
      && soundEnabled) {
      const audio = new Audio(doneSound);
      void audio.play().catch(() => undefined);
    }
    previousStatus.current = status;
  }, [status, soundEnabled]);

  const changeDatasetConsent = (accepted) => {
    setDatasetConsent(accepted);
    setDatasetAcceptedState(accepted);
  };

  const selectFile = async (file) => {
    setFileError(null);
    try {
      await session.selectFile(file);
    } catch (error) {
      setFileError({
        details: {
          error: getErrorDiagnostics(error),
          file: getMediaFileDiagnostics(file),
        },
        message: error?.message || 'No s’ha pogut seleccionar el fitxer.',
      });
    }
  };

  const changeLanguage = (event) => {
    const nextLanguage = event.target.value;
    if (nextLanguage !== 'ca' && !isPremium) {
      onRequirePremium();
      return;
    }
    onLanguageChange(nextLanguage);
  };

  if (status === TRANSCRIPTION_STATUS.COMPLETE) {
    return (
      <div className="output-section">
        <TranscriptionResult
          audioUrl={session.audioUrl}
          chunks={session.chunks}
          fileName={session.file?.name}
          language={language}
          onReset={session.reset}
          onSoundChange={changeSound}
          soundEnabled={soundEnabled}
        />
      </div>
    );
  }

  if (active) {
    return (
      <div className="output-section">
        <TranscriptionProgress
          audioUrl={session.audioUrl}
          onCancel={holdingResult ? session.reset : session.cancel}
          onSoundChange={changeSound}
          soundEnabled={soundEnabled}
          status={status}
        />
      </div>
    );
  }

  return (
    <div className="input-card">
      <FilePicker disabled={active} file={session.file} onSelect={selectFile} />
      {fileError && (
        <UiError className="transcription-error" context="file-selection" details={fileError.details}>
          {fileError.message}
        </UiError>
      )}
      <RangeSelector
        disabled={active}
        duration={session.duration}
        startSeconds={session.startSeconds}
        endSeconds={session.endSeconds}
        setStartSeconds={session.setStartSeconds}
        setEndSeconds={session.setEndSeconds}
      />
      {session.file && (
        <DatasetConsent accepted={datasetAccepted} enabled={datasetEnabled} isPremium={isPremium} language={language} onChange={changeDatasetConsent} />
      )}
      <div className="controls-container">
        <div className="language-selector">
          <label htmlFor="language">Idioma:</label>
          <select id="language" value={language} disabled={active} onChange={changeLanguage}>
            {LANGUAGES.map(([code, label]) => <option key={code} value={code}>{label}{code !== 'ca' ? ' · PRO' : ''}</option>)}
          </select>
        </div>
        <div className="mode-selector">
          <label htmlFor="mode">Mode:</label>
          <select id="mode" value={mode} disabled={active} onChange={(event) => onModeChange(event.target.value)}>
            <option value="rapid">Ràpid</option><option value="quality">Qualitat</option>
          </select>
        </div>
        <button className={`transcribe-btn ${isPremium ? 'premium' : ''}`} disabled={!canStartTranscription(session.status) || (language !== 'ca' && !isPremium)} onClick={() => session.start({
          language,
          mode,
          token: userData?.accessToken,
          onContribution: shouldContribute
            ? (sourceFile) => contribute(sourceFile, { durationSeconds: session.duration })
            : undefined,
        })}>Transcriu</button>
        <div className={`status-indicator ${session.status === TRANSCRIPTION_STATUS.READY ? 'ready' : ''}`} aria-live="polite">
          <span className="status-dot" />
          {session.status === TRANSCRIPTION_STATUS.IDLE && 'Carrega un fitxer per començar'}
          {session.status === TRANSCRIPTION_STATUS.READY && 'Preparat per transcriure'}
          {session.status === TRANSCRIPTION_STATUS.PREPARING && 'Preparant els fragments…'}
          {session.status === TRANSCRIPTION_STATUS.TRANSCRIBING && 'Transcripció en curs…'}
          {session.status === TRANSCRIPTION_STATUS.CANCELLED && 'Transcripció cancel·lada'}
          {session.status === TRANSCRIPTION_STATUS.ERROR && 'La transcripció ha fallat'}
        </div>
      </div>
      {session.status === TRANSCRIPTION_STATUS.ERROR && (
        <UiError
          className="transcription-error"
          context="transcription"
          details={{
            error: session.errorDetails,
            file: getMediaFileDiagnostics(session.file),
            transcription: {
              currentChunk: session.currentChunk,
              durationSeconds: session.duration,
              endSeconds: session.endSeconds,
              language,
              mode,
              startSeconds: session.startSeconds,
              status: session.status,
              totalChunks: session.totalChunks,
            },
          }}
        >
          {session.error}
        </UiError>
      )}
    </div>
  );
}
